import type { PageSchema } from "../blocks/types";

/**
 * `/pt/casar-em-portugal-documentos/` — юридические требования к браку в
 * Португалии. Ключ реестра `legal`, английский двойник — `/legal-requirements/`
 * (`src/pages/LegalRequirements.tsx`).
 *
 * Все факты взяты из английской страницы: отсутствие ценза оседлости, срок
 * годности документов (шесть месяцев, у части консерваторий три), календарь
 * «искать за 9 месяцев / запрашивать certidões за 5–6 / открывать процесс в
 * пределах шести месяцев до даты», перечень документов, восемь шагов процедуры,
 * особенности США (Affidavit вместо CNI, апостиль на уровне штата), Великобритании
 * (28 дней до выдачи CNI, апостиль FCDO) и ЕС (многоязычные формуляры), запрет
 * католической церемонии вне освящённой церкви, обязательный присяжный
 * переводчик и дисклеймер об отсутствии юридической консультации. Суммы
 * регистрации (120 € / 200 €, конвенция +100 / +160 / +30), окно processo
 * preliminar 1–6 месяцев, портал Civil Online и режимы имущества — те же
 * проверенные цифры, что уже опубликованы на соседних PT-страницах
 * (`money-casamento.ts`, `organizacao-de-casamentos.ts`).
 *
 * Форма страницы: сводный ответ → чек-лист документов → таблица типов церемонии
 * → календарь из восьми шагов → таблица по гражданствам → решение civil/simbólica
 * → FAQ. Это самая фактурная страница набора, и запрос у неё процедурный, а не
 * сервисный: машина должна извлечь список документов и сумму построчно, поэтому
 * витрины услуг здесь нет вовсе, а каждый числовой факт стоит в своей строке
 * таблицы, списка или шага.
 */
export const casarEmPortugalDocumentos: PageSchema = {
  en: { kind: "component" },
  pt: {
    kind: "blocks",
    meta: {
      title: "Documentos para Casar em Portugal: Prazos e Custos | Event Boutique",
      description:
        "Que documentos pede a conservatória, quando abrir o processo preliminar, quanto tempo dura a aprovação e quanto custa o registo civil: 120 €, ou 200 € fora da conservatória.",
      headQuery: "documentos para casar em portugal",
    },
    blocks: [
      {
        type: "hero",
        eyebrow: "Guia de planeamento",
        h1: "Documentos, prazos e custos para casar em Portugal",
        lede:
          "O que a conservatória exige, em que mês pedir cada papel e o que muda consoante o passaporte.",
        image: "images-ready-31-03/loc-lisbon-panoramic-golden-hour.webp",
        imageAlt: "Vista panorâmica de Lisboa ao fim da tarde",
      },
      {
        type: "answerFirst",
        paragraphs: [
          "O conjunto de documentos é sempre o mesmo, e é ele que marca o ritmo de tudo o resto: passaporte, título ou autorização de residência ou documento equivalente de cada um, certidão do registo de nascimento na forma exigida pela lei do país de origem, certificado de capacidade matrimonial que ateste o estado livre e, quando houve casamento anterior, certidão de divórcio ou de óbito. O certificado de capacidade matrimonial tem de ter sido emitido há menos de seis meses, salvo se o país de origem fixar outro prazo de validade. A tradução certificada é exigida aos documentos que não estejam em português, inglês, francês ou espanhol; a legalização de documentos estrangeiros segue regras próprias e confirma-se com a conservatória.",
          "O processo preliminar de casamento abre-se em qualquer Conservatória do Registo Civil ou pelo portal Civil Online. Aprovado o processo, o casamento deve celebrar-se nos seis meses seguintes ao despacho favorável, e o IRN recomenda tratá-lo com pelo menos um mês de antecedência. O processo e o registo de casamento custam 120 €, ou 200 € quando a celebração é fora da conservatória, fora do horário de funcionamento ou ao fim de semana, e decorre em português, o que obriga por lei a tradutor certificado se algum dos noivos não dominar a língua. Convém começar a tratar do assunto entre seis e nove meses antes, por causa dos prazos de validade das certidões e da disponibilidade de marcação nas conservatórias.",
        ],
      },
      {
        type: "procedural",
        heading: "Documentos para um casal estrangeiro",
        intro:
          "Os requisitos variam com a nacionalidade, mas a base repete-se em quase todos os casais que chegam de fora.",
        items: [
          "Passaporte válido de cada um dos noivos, com margem confortável até à data de validade, ou título de residência para quem já reside em Portugal",
          "Certidão do registo de nascimento, na forma exigida pela lei do país de origem",
          "Certificado de capacidade matrimonial, o documento que atesta que cada um está livre para casar",
          "Affidavit of Eligibility to Marry em vez do certificado, no caso dos cidadãos norte-americanos, porque nos Estados Unidos não existe registo civil central",
          "Certidão de divórcio ou de óbito, quando há um casamento anterior a dar como dissolvido",
          "Apostila da Convenção de Haia em cada documento estrangeiro, ou legalização no consulado quando o país emissor não é signatário",
          "Tradução certificada para português de todos os documentos que não estejam nessa língua",
        ],
        note:
          "Os documentos são normalmente aceites se tiverem sido emitidos há menos de seis meses à data do casamento, e há conservatórias que exigem três meses para documentos específicos. A lista que vale é sempre a da conservatória do concelho onde a cerimónia se realiza, confirmada junto do consulado respetivo. Este guia é informativo e não constitui aconselhamento jurídico.",
      },
      {
        type: "table",
        heading: "Tipos de cerimónia e o que cada um permite",
        columns: ["Cerimónia", "Onde pode realizar-se", "Efeito legal", "O que exige"],
        rows: [
          [
            "Civil",
            "Na Conservatória do Registo Civil ou num espaço privado, como um palácio em Sintra ou uma villa no Algarve, se o conservador aceitar a deslocação",
            "Casamento legalmente válido, reconhecido no país de origem dos noivos",
            "Processo preliminar aberto em qualquer conservatória, com o casamento a celebrar-se nos seis meses seguintes ao despacho favorável; 120 € em dia útil na conservatória e 200 € fora dela; tradutor certificado se algum dos noivos não falar português",
          ],
          [
            "Católica",
            "Dentro de uma igreja consagrada, nunca ao ar livre, na praia ou num jardim privado",
            "Produz efeitos civis sem cerimónia separada, desde que a documentação seja articulada entre a paróquia e o registo civil",
            "Processo paroquial a correr em paralelo com a documentação civil",
          ],
          [
            "Outras confissões reconhecidas pelo Estado",
            "No local de culto da confissão",
            "O efeito legal resulta habitualmente do casamento civil, celebrado primeiro",
            "Casamento civil antes do rito religioso, com a mesma documentação",
          ],
          [
            "Simbólica",
            "Em qualquer lugar e a qualquer hora: falésia, jardim, adega, terraço",
            "Nenhum. O ato legal é tratado à parte, no país de origem ou num dia útil em Portugal",
            "Nada perante o Estado. O guião é escrito de raiz e pode ser conduzido por alguém próximo do casal",
          ],
        ],
        note:
          "Muitos casais internacionais separam as duas coisas: tratam do ato legal num dia útil e celebram em Portugal com uma cerimónia simbólica, sem restrição de hora nem de local.",
      },
      {
        type: "steps",
        heading: "O processo, passo a passo",
        intro:
          "Uma sequência com datas evita a fricção das últimas semanas. Este é o percurso de um casamento civil celebrado em Portugal.",
        steps: [
          {
            when: "Cerca de 9 meses antes",
            title: "Decidir o tipo de cerimónia",
            body: [
              "A escolha entre casamento civil em Portugal e cerimónia simbólica, com o ato legal tratado no país de origem, condiciona tudo o resto: a lista de documentos, o calendário e a liberdade de local e de hora.",
              "É a decisão que se toma primeiro, porque é a que muda a natureza de todo o trabalho seguinte.",
            ],
            risk: "Decidida tarde, obriga a comprimir apostilas e traduções nas últimas semanas.",
          },
          {
            when: "6 a 9 meses antes",
            title: "Confirmar os prazos na conservatória do concelho",
            body: [
              "Os prazos de marcação e as exigências documentais variam de conservatória para conservatória. Confirmam-se junto da do concelho onde a cerimónia se vai realizar, e não junto de outra qualquer.",
              "É também aqui que se percebe se o conservador se desloca ao espaço escolhido e em que condições.",
            ],
          },
          {
            when: "5 a 6 meses antes",
            title: "Pedir as certidões",
            body: [
              "O certificado de capacidade matrimonial pede-se nesta altura, e não antes: a lei exige que tenha sido emitido há menos de seis meses, salvo se o país de origem fixar outro prazo de validade.",
              "Antes disso vale a pena reunir a informação e perceber onde cada documento se pede, sobretudo quando os noivos nasceram em países diferentes.",
            ],
            risk: "Certidões pedidas com um ano de antecedência caducam antes do dia, e o pedido e a tradução repetem-se do início.",
          },
          {
            when: "Assim que as certidões chegam",
            title: "Apostilar e traduzir",
            body: [
              "Cada documento estrangeiro recebe a apostila da Convenção de Haia junto da autoridade competente do país que o emitiu, ou legalização consular quando esse país não é signatário.",
              "Só depois seguem para tradução certificada em português. As duas etapas são sequenciais, porque a apostila é aposta no original e traduz-se o conjunto.",
            ],
          },
          {
            when: "1 a 6 meses antes",
            title: "Abrir o processo preliminar",
            body: [
              "O processo abre-se na Conservatória do Registo Civil ou pelo portal Civil Online, e tem de ser aberto dentro dos seis meses que antecedem a data do casamento.",
              "É neste momento que se declara o regime de bens. Se nada for declarado aplica-se a comunhão de adquiridos, o regime supletivo; as alternativas são a comunhão geral e a separação de bens, formalizadas por convenção antenupcial.",
            ],
            money:
              "120 € em dia útil na conservatória, 200 € fora dela, ao fim de semana ou em feriado. A convenção antenupcial acrescenta 100 € em regime típico, 160 € em regime atípico e 30 € pelo registo da convenção celebrada perante entidade diferente da conservatória, valor que acresce ao preço dessa entidade.",
          },
          {
            when: "Depois da aprovação do processo",
            title: "Marcar o conservador",
            body: [
              "Com o processo aprovado, fixam-se a data e a hora da celebração e, quando a cerimónia é fora da conservatória, a deslocação do conservador ao espaço.",
            ],
          },
          {
            when: "No dia",
            title: "A cerimónia e a assinatura",
            body: [
              "A celebração decorre em português. Se algum dos noivos não dominar a língua, a lei obriga a tradutor certificado, porque o conservador tem de garantir que ambos compreendem o contrato que estão a assinar.",
              "O registo de casamento é assinado no próprio dia, perante as testemunhas.",
            ],
          },
          {
            when: "Depois do casamento",
            title: "Levantar a certidão",
            body: [
              "A conservatória emite a certidão de casamento. Na versão internacional, é o documento que faz o casamento ser reconhecido no país de origem dos noivos.",
            ],
          },
        ],
      },
      {
        type: "table",
        heading: "O que muda consoante o passaporte",
        columns: ["Nacionalidade", "Documento de estado livre", "Particularidade do processo"],
        rows: [
          [
            "Estados Unidos",
            "Affidavit of Eligibility to Marry, assinado na embaixada ou no consulado",
            "Não existe registo civil central norte-americano, pelo que não há certificado de capacidade matrimonial a pedir. O affidavit assina-se presencialmente em Lisboa ou no Porto, com marcação prévia. Como as certidões de nascimento são emitidas ao nível do estado, a apostila obtém-se junto do Secretary of State do estado que emitiu o documento.",
          ],
          [
            "Reino Unido",
            "Certificate of No Impediment (CNI)",
            "Publica-se o aviso de casamento no register office local. O CNI é emitido 28 dias depois e tem ainda de ser apostilado pelo Foreign, Commonwealth and Development Office.",
          ],
          [
            "União Europeia, com residência fora de Portugal",
            "Certificado de capacidade matrimonial do país de origem",
            "É o percurso mais direto: os formulários multilingues dispensam a tradução numa parte dos casos. Ainda assim, o registo de origem e o registo português têm de ser articulados, para que os dados coincidam exatamente em ambos.",
          ],
        ],
        note:
          "As exigências das embaixadas e dos consulados mudam sem aviso. Antes de pedir seja o que for, confirmem a lista em vigor junto do consulado respetivo.",
      },
      {
        type: "questions",
        heading: "Civil ou simbólica: como decidir",
        intro:
          "A pergunta prática não é qual das duas vale mais, mas qual das duas serve o dia que estão a imaginar.",
        items: [
          {
            q: "Queremos casar numa falésia, num jardim ou numa adega",
            a: "A cerimónia simbólica não tem restrição de local nem de hora. A civil pode realizar-se fora da conservatória, mas depende de o conservador aceitar a deslocação e do horário que tiver disponível. A católica está limitada ao interior de uma igreja consagrada.",
          },
          {
            q: "Não queremos que a papelada condicione a data",
            a: "Tratar o ato legal no país de origem separa os dois calendários. O dia em Portugal deixa de depender de uma apostila em atraso ou de uma tradução que chegou tarde, e passa a depender apenas da produção.",
          },
          {
            q: "Queremos votos escritos por nós e alguém próximo a conduzir",
            a: "O casamento civil segue a fórmula legal e é conduzido pelo conservador. Numa cerimónia simbólica o guião escreve-se de raiz, cabem tradições de família e conduz quem os noivos escolherem.",
          },
          {
            q: "Queremos que o casamento seja legal em Portugal",
            a: "Então o percurso é o civil, com o processo preliminar aberto em qualquer conservatória e a documentação traduzida quando não esteja em português, inglês, francês ou espanhol. É possível para um casal estrangeiro sem residência no país, e o que exige é calendário.",
          },
          {
            q: "Somos católicos e queremos casar pela igreja",
            a: "O casamento católico produz efeitos civis sem cerimónia separada, desde que o processo seja articulado entre a paróquia e o registo civil. Em contrapartida, tem de decorrer dentro de uma igreja, o que exclui os cenários ao ar livre.",
          },
        ],
      },
      {
        type: "prose",
        heading: "O que fazemos nesta parte",
        paragraphs: [
          "A Event Boutique é um estúdio de produção e de design, não um escritório de advogados nem uma conservatória. O que fazemos é integrar o calendário legal no calendário do casamento, para que a documentação não apareça de surpresa a seis semanas da data.",
          "Na prática: dizemos em que mês pedir cada certidão para que não caduque antes do dia, identificamos a conservatória do concelho onde a cerimónia se realiza e mantemos o contacto com ela, contratamos os tradutores certificados para a celebração e desenhamos o momento da assinatura dentro do programa, em vez de o deixar como uma formalidade encaixada a meio da tarde.",
          "Não damos aconselhamento jurídico nem garantimos a aceitação de qualquer documento. As leis do casamento e as exigências das embaixadas mudam sem aviso prévio, por isso os requisitos em vigor confirmam-se sempre junto da Conservatória do Registo Civil e do consulado respetivo.",
        ],
      },
      {
        type: "faq",
        heading: "Perguntas frequentes",
        items: [
          {
            q: "Um casal estrangeiro pode casar em Portugal?",
            a: "Sim, e não é preciso residir no país. Com a documentação exigida — certidão do registo de nascimento, certificado de capacidade matrimonial e traduções certificadas — o casamento é legalmente válido e reconhecido no país de origem dos noivos.",
          },
          {
            q: "Com quanto tempo se começa a reunir os documentos para casar em Portugal?",
            a: "A pesquisa começa cerca de nove meses antes, mas as cópias oficiais das certidões e do certificado de capacidade matrimonial pedem-se apenas cinco a seis meses antes da data. As autoridades portuguesas exigem habitualmente documentos emitidos há menos de seis meses à data do casamento, para que a informação esteja atualizada.",
          },
          {
            q: "Quanto custa o registo civil?",
            a: "O processo e o registo de casamento custam 120 €. São 200 € quando a celebração acontece fora da conservatória, nesta mas fora do horário de funcionamento, ou ao sábado, domingo ou feriado — valor que substitui os 120 €, não acresce. A convenção antenupcial acrescenta 100 € em regime típico, 160 € em regime atípico e 30 € pelo registo da convenção celebrada perante entidade diferente da conservatória, valor que acresce ao preço dessa entidade. São valores do ato de registo e não têm relação com o custo da celebração.",
          },
          {
            q: "Compensa casar legalmente em casa e fazer uma cerimónia simbólica em Portugal?",
            a: "Para muitos casais, sim. Dispensa a burocracia portuguesa e liberta para o desenho do dia o orçamento que iria para traduções e legalizações. Do lado dos convidados a diferença não se nota, porque uma cerimónia simbólica tem exatamente o mesmo aspeto de uma cerimónia legal.",
          },
          {
            q: "Uma cerimónia simbólica tem o peso de uma cerimónia a sério?",
            a: "O guião de uma cerimónia simbólica é escrito para o casal em concreto: os votos são vossos, cabem tradições de família e conduz alguém que vos conhece. O que fica de fora é apenas a fórmula legal, que é tratada noutro dia.",
          },
          {
            q: "É preciso tradutor num casamento civil?",
            a: "Sim, se algum dos noivos não falar português com fluência. A celebração decorre em português e o conservador tem de garantir, por lei, que ambos compreendem o contrato que estão a assinar. Contratamos tradutores habituados a cerimónias, para que o tom da celebração se mantenha.",
          },
          {
            q: "Pode casar-se numa praia em Portugal?",
            a: "No civil, sim, se o conservador aceitar o local. No casamento católico não, porque a cerimónia tem de decorrer dentro de uma igreja. É por isso que grande parte dos casamentos de praia em Portugal são simbólicos.",
          },
        ],
      },
      {
        type: "related",
        heading: "Continuar a leitura",
        links: [
          { routeKey: "service-wedding-planning", label: "Como organizar um casamento em Portugal, passo a passo" },
          { routeKey: "pt-money-casamento", label: "Quanto custa um casamento em Portugal" },
          { routeKey: "venues", label: "Quintas e palácios para casamentos, por região" },
        ],
      },
      {
        type: "cta",
        heading: "Falar sobre o vosso casamento",
        body:
          "Com a data, a região e a nacionalidade de cada um dos noivos conseguimos dizer que documentos são precisos, em que mês pedir cada um e o que a conservatória do concelho vai exigir.",
        routeKey: "contact",
        label: "Falar connosco",
      },
    ],
  },
};
